import { supabase } from '../lib/supabase';
import { SK } from '../config/constants';

export const fetchProfile = async (userId) => {
  const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
  return data;
};

// tenant_id can't be written directly by an authenticated client (column-level privilege,
// see 20260812_profiles_column_lockdown.sql) — self-inserting/updating it would let anyone
// join any tenant. claim_tenant() is a SECURITY DEFINER RPC that sets it exactly once, from
// auth.uid(), and is a no-op if the profile already belongs to a tenant.
export const ensureProfile = async (userId, email, tenantId) => {
  const { data: existing } = await supabase
    .from('profiles').select('*').eq('id', userId).maybeSingle();

  if (!existing) {
    await supabase.from('profiles').insert({ id: userId, email });
    if (tenantId) await supabase.rpc('claim_tenant', { p_tenant_id: tenantId });
    const { data } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    return data;
  }

  if (!existing.tenant_id && tenantId) {
    await supabase.rpc('claim_tenant', { p_tenant_id: tenantId });
    const { data } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    return data || existing;
  }

  return existing;
};

// ─── Per-product debounce ─────────────────────────────────────
const _debounceMap = {};
const DEBOUNCE_MS  = 600;

export const deleteProducts = async (ids) => {
  if (!ids?.length) return;
  const { error } = await supabase.from('products').delete().in('id', ids);
  if (error) console.error('[deleteProducts]', error);
};

export const deleteCollection = async (collectionId) => {
  const { error: pe } = await supabase.from('products').delete().eq('collection_id', collectionId);
  if (pe) console.error('[deleteCollection] products', pe);
  const { error: ce } = await supabase.from('collections').delete().eq('app_id', collectionId);
  if (ce) console.error('[deleteCollection] collection', ce);
};

export const saveProduct = (product, collectionId) => {
  clearTimeout(_debounceMap[product.id]);
  _debounceMap[product.id] = setTimeout(async () => {
    delete _debounceMap[product.id];
    const { error } = await supabase.from('products').upsert(
      { id: product.id, collection_id: collectionId, data: product, updated_at: new Date().toISOString() },
      { onConflict: 'id' }
    );
    if (error) console.error('[saveProduct]', error);
  }, DEBOUNCE_MS);
};

export const saveProductsBulk = async (products, collectionId) => {
  if (!products?.length || !collectionId) return;
  const now  = new Date().toISOString();
  const rows = products.map(p => ({ id: p.id, collection_id: collectionId, data: p, updated_at: now }));
  const CHUNK = 100;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { error } = await supabase.from('products').upsert(rows.slice(i, i + CHUNK), { onConflict: 'id' });
    if (error) console.error('[saveProductsBulk]', error);
  }
};

// ─── Metadata save: collections + singleton tables ────────────
// Products are saved separately so concurrent editors never overwrite each other.
export const storeSave = async ({ collections, activeCollIdx, priceTable, sizeTable, launchCalendar, tenantId, lsKey }) => {
  const key = lsKey || SK;
  // Write-ahead buffer: survives a hard reload within the debounce window.
  localStorage.setItem(key, JSON.stringify({
    collections, activeCollIdx, priceTable, sizeTable, launchCalendar, _savedAt: Date.now(),
  }));

  const now = new Date().toISOString();

  const results = await Promise.all([
    ...collections.map(c =>
      supabase.from('collections').upsert(
        { app_id: c.id, name: c.name, month: c.month || null, springboard: c.springboard || null, tenant_id: tenantId, updated_at: now },
        { onConflict: 'app_id' }
      )
    ),
    priceTable && tenantId
      ? supabase.from('price_table').upsert({ tenant_id: tenantId, data: priceTable, updated_at: now }, { onConflict: 'tenant_id' })
      : null,
    sizeTable && tenantId
      ? supabase.from('size_table').upsert({ tenant_id: tenantId, data: sizeTable, updated_at: now }, { onConflict: 'tenant_id' })
      : null,
    launchCalendar && tenantId
      ? supabase.from('launch_calendar').upsert({ tenant_id: tenantId, events: launchCalendar, updated_at: now }, { onConflict: 'tenant_id' })
      : null,
  ]);

  results.forEach(r => { if (r?.error) console.error('[storeSave]', r.error); });
};

// ─── Load ─────────────────────────────────────────────────────
// Supabase is the single source of truth. localStorage is only a write-ahead
// buffer (survives hard reloads within the debounce window) and a one-time
// migration path for data that pre-dates Supabase persistence.
export const storeLoad = async (tenantId, lsKey) => {
  const key = lsKey || SK;

  // Fetch everything from Supabase in parallel.
  let colls, productRows, pt, st, cal;
  try {
    const { data, error } = await supabase
      .from('collections').select('*').eq('tenant_id', tenantId).order('created_at');
    if (error) throw error;
    colls = data || [];

    const ids = colls.map(c => c.app_id).filter(Boolean);
    const [prodRes, ptRes, stRes, calRes] = await Promise.all([
      ids.length
        ? supabase.from('products').select('id, collection_id, data').in('collection_id', ids)
        : Promise.resolve({ data: [] }),
      supabase.from('price_table').select('*').eq('tenant_id', tenantId).maybeSingle(),
      supabase.from('size_table').select('*').eq('tenant_id', tenantId).maybeSingle(),
      supabase.from('launch_calendar').select('*').eq('tenant_id', tenantId).maybeSingle(),
    ]);
    productRows = prodRes.data || [];
    pt  = ptRes.data;
    st  = stRes.data;
    cal = calRes.data;
  } catch (e) {
    console.error('[storeLoad] Supabase fetch failed — falling back to localStorage:', e);
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  }

  // Read localStorage (write-ahead buffer).
  let ls = null;
  try { const raw = localStorage.getItem(key); if (raw) ls = JSON.parse(raw); } catch (_) {}

  const dbCount = productRows.length;
  const lsCount = ls ? (ls.collections || []).reduce((n, c) => n + (c.products || []).length, 0) : 0;

  // If localStorage has more products than DB, it is the authoritative source.
  // Push directly to Supabase WITHOUT touching localStorage so it remains as a retry
  // buffer if the Supabase write fails. On the next load, if lsCount == dbCount the
  // write succeeded and localStorage is cleared; if lsCount > dbCount we retry.
  if (ls && lsCount > dbCount) {
    const now = new Date().toISOString();
    Promise.all(
      (ls.collections || []).map(c =>
        supabase.from('collections').upsert(
          { app_id: c.id, name: c.name, month: c.month || null, springboard: c.springboard || null, tenant_id: tenantId, updated_at: now },
          { onConflict: 'app_id' }
        )
      )
    ).catch(e => console.error('[storeLoad] push collections:', e));

    (ls.collections || []).forEach(c =>
      saveProductsBulk(c.products, c.id).catch(e => console.error('[storeLoad] push products:', e))
    );

    if (!colls.length) return ls;

    // Merge: all DB collections, prefer localStorage products where DB has none yet.
    const byDB = {};
    for (const row of productRows) {
      if (!byDB[row.collection_id]) byDB[row.collection_id] = [];
      byDB[row.collection_id].push(row.data);
    }
    const lsMap = Object.fromEntries((ls.collections || []).map(c => [c.id, c]));
    return {
      collections: colls.map(c => ({
        id:          c.app_id,
        name:        c.name,
        month:       c.month || null,
        products:    byDB[c.app_id]?.length ? byDB[c.app_id] : (lsMap[c.app_id]?.products || []),
        springboard: c.springboard || null,
      })),
      priceTable:     pt?.data   || null,
      sizeTable:      st?.data   || null,
      launchCalendar: cal?.events || [],
    };
  }

  // Supabase is authoritative — discard stale localStorage.
  localStorage.removeItem(key);

  if (!colls.length) return null;

  const byCollection = {};
  for (const row of productRows) {
    if (!byCollection[row.collection_id]) byCollection[row.collection_id] = [];
    byCollection[row.collection_id].push(row.data);
  }

  return {
    collections: colls.map(c => ({
      id:          c.app_id,
      name:        c.name,
      month:       c.month || null,
      products:    byCollection[c.app_id] || [],
      springboard: c.springboard || null,
    })),
    priceTable:     pt?.data   || null,
    sizeTable:      st?.data   || null,
    launchCalendar: cal?.events || [],
  };
};
